import { ShapeInfo } from 'kld-intersections';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { ObjectManager } from '../../../concepts/objects';
import { Geometry, IntersectResult } from '../../../platform/geom/Geometry';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { AlertHub } from '../../packages/alerts/AlertHub';
import { Button } from '../../packages/controls/Button';
import { SmartBox } from '../../packages/controls/SmartBox';
import { SmartButton } from '../../packages/controls/SmartButton';
import { Frame } from '../../packages/frame/Frame';
import { Graphic } from '../../packages/graphics/Graphic';
import { CardPack } from '../../packages/hand/CardPack';
import { DropZone } from '../../packages/hand/DropZone';
import { Shape } from '../../packages/shapes/Shape';
import { Text } from '../../packages/text/Text';
import { ToastHub } from '../../packages/toast/ToastHub';
import { Zone } from '../../packages/zones/Zone';
import { Camera } from '../Camera';
import { DebugOverlay } from '../DebugOverlay';
import { Physical, RelativeTo } from '../entities/Physical';
import { Physicals } from '../entities/Physicals';
import { Snappable } from '../entities/Snappable';
import { LockLayoutAlert } from '../workflow/LockLayoutAlert';
import { resolveSubjects } from './CommandUtil';


const { createAtCameraPosition } = Physicals;

export const createButton = defineCommand(
    'createButton',
    'Create: Button',
    (r, [script]: [string?]) => {
        createAtCameraPosition(r, Button, nc => {
            nc.width = 124;
            nc.height = 50;
            if (script) {
                nc.commandScript = script;
            }
        });
    },
    [
    ]
);

export const createCameraButton = defineCommand(
    'createCameraButton',
    'Create: Camera Button',
    (r) => {
        const camera = r.call(Camera);
        const [x, y, w, h] = camera.getModelFrame().toArray();

        createAtCameraPosition(r, Button, nc => {
            nc.content = 'Next';
            nc.width = 124;
            nc.height = 50;
            nc.commandScript = `cameraMoveToFrame ${x} ${y} ${w} ${h}`;
        });
    },
    [
    ]
);

// export const createPicker = defineCommand(
//     'createPicker',
//     'Create: Picker',
//     (r) => {

//         const objects = r.call(ObjectManager);
//         objects.update(u => {

//             const createOpt = (key: string, label: string, image: string) => {
//                 const opt = u.create(PickerOption);
//                 opt.key = key;
//                 opt.label = label;
//                 opt.image = image;
//                 return opt;
//             };

//             const opts = u.create(PickerOptionGroup);
//             opts.add(createOpt('v1', 'Stephen Whitfield', 'deqo://img/stephen'));
//             opts.add(createOpt('v2', 'Jamie Hanratty', 'deqo://img/jamie'));
//             opts.add(createOpt('v3', 'Lisa Angel', 'deqo://img/lisa'));

//             const pkr = u.create(Picker);
//             pkr.x = 0;
//             pkr.y = 0;
//             pkr.width = 50;
//             pkr.height = 50;
//             pkr.options = opts.id;
//             pkr.style = PickerStyle.image;
//             objects.get('root', Root).add(pkr);

//         });
//     },
//     [
//         // pointStep('Enter size in the following format: w, h')
//     ]
// );

export const lockToggle = defineCommand(
    'lockToggle',
    'Lock: Toggle',
    (r, [targetIds]: [string[]?]) => {

        const objects = r.call(ObjectManager);
        const subjects = resolveSubjects(r, targetIds);
        if (!subjects.length) return;

        const value = !subjects.find(x => x.locked);
        objects.update(() => {
            for (const s of subjects) {
                s.locked = value;
            }
        });
    });

export const lockSet = defineCommand(
    'lockSet',
    'Lock: Set...',
    (r, [value, targetIds]: [boolean, string[]?]) => {

        const objects = r.call(ObjectManager);
        const subjects = resolveSubjects(r, targetIds);
        if (!subjects.length) return;

        objects.update(() => {
            for (const s of subjects) {
                if (s.locked !== value) {
                    s.locked = value;
                }
            }
        });
    });

export const lockSetAll = defineCommand(
    'lockSetAll',
    'Lock: Set All...',
    (r, [value]: [boolean]) => {

        const objects = r.call(ObjectManager);
        const toast = r.call(ToastHub);

        const subjects = objects.getAll(Physical);
        if (!subjects.size) return;

        objects.update(() => {
            for (const s of subjects) {
                if (s.locked !== value) {
                    s.locked = value;
                }
            }
        });

        toast.invokeAndSend(value ? 'design:lockAll' : 'design:unlockAll', {});
    });

export const lockSetLayoutIfNotLocked = defineCommand(
    'lockSetLayoutIfNotLocked',
    null,
    r => {

        const alerts = r.call(AlertHub);
        const objects = r.call(ObjectManager);

        const subjects = [
            ...Array.from(objects.getAll(Graphic)),
            ...Array.from(objects.getAll(Text)),
            ...Array.from(objects.getAll(Shape)),
            ...Array.from(objects.getAll(Zone)),
        ];
        if (!subjects.length) return;
        if (!subjects.find(x => !x.locked)) return;

        alerts.invoke({
            type: 'dialog',
            modal: true,
            body: LockLayoutAlert,
            delay: 40000,
        });
    });

export const lockSetLayout = defineCommand(
    'lockSetLayout',
    'Lock: Set Layout...',
    (r, [value]: [boolean]) => {

        const objects = r.call(ObjectManager);
        const toast = r.call(ToastHub);

        const subjects = new Set([
            ...Array.from(objects.getAll(Snappable)),
            ...Array.from(objects.getAll(DropZone)),
            ...Array.from(objects.getAll(CardPack)),
            ...Array.from(objects.getAll(SmartButton)),
            ...Array.from(objects.getAll(SmartBox)),
            ...Array.from(objects.getAll(Frame)),
            ...Array.from(objects.getAll(Shape)),
            ...Array.from(objects.getAll(Text)),
            ...Array.from(objects.getAll(Zone)),
        ]);
        if (!subjects.size) return;

        objects.update(() => {
            for (const s of subjects) {
                if (s.locked !== value) {
                    s.locked = value;
                }
            }
        });

        toast.invokeAndSend('design:lockLayout', {});
    });

/*
export const group = defineCommand(
    'group',
    'Group',
    (r, [targets]: [string[]?]) => {

        // Groups are a special case in that their contents are positioned relative to the parent of the group,
        // rather than the group itself.  This is so they can easily by dynamically resized and moved if the contents
        // of the group are transformed.  Performing this with relatively positioned children would require automatic
        // constraints to be applied to all children so that when you are moving a single child the others don't move
        // as a result of the re-dimensioning of the group.

        // It's much simpler just to make Groups a sort of pass-through element that hugs it's children but the
        // children are relative to it's parent.  Therefore, creating a group essentially involves extracting the target
        // components and remounting them into a new Group instance and keeping their current transform.  We must cater
        // for selections that span different branches of the component tree.  To do this we only _target_ the items that
        // are the top most nodes in their respective branches.  So for example, if nodes A1 and A3 are targeted but A2
        // not, and B1 targeted; then we take only A1 and B1 for consideration in the group manuvour since all their
        // children will automatically be brought along for the ride.

        const session = r.call(EditorSession);
        const objects = r.call(ObjectManager);

        const root = session.get(RootOverride) ?? objects.get('root', Root);
        const subjects = resolveSubjects(r, targets);
        if (!subjects.length) return;

        // Select only the top level components from the selection sorted by their order
        // const tlTargets = targets
        //     .filter(c => !targets.find(x => c.isDescenantOf(x)))
        //     .sort((a, b) => a.order - b.order);

        // Compute the bounds of the targets (which will be the group area)
        // const bounds = Rect.fromMany(tlTargets.map(x => x.bounds(root)));

        // Clear the selection before we unmount because it can cause errors
        session.apply(Selection, s => s.clear());

        objects.update(u => {

            // Create and position the group
            const ng = u.create(Group);

            // For each target, unmount then remount inside the group
            for (const s of subjects) {
                ng.add(s.removeSelf());
            }

            // Mount and select the group
            root.add(ng);
            session.apply(Selection, s => s.replace([ng]));

        });
    },
    []
);

export const ungroup = defineCommand(
    'ungroup',
    'Ungroup',
    (r, [targets]: [string[]?]) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);

        const subjects = resolveSubjects(r, targets);
        if (!subjects.length) return;

        // Filter the selection into groups and non-groups
        const [groups, others] = Arr.split(subjects, x => x.is(Group));

        // Clear the selection before we unmount because it can cause errors
        dispatch(clearSelection);

        // Keep track of affected components so we can reselect them all
        const affected = [] as Physical[];

        objects.update(_u => {

            // For each group, trash it's shit
            for (const gp of groups) {

                const children = gp.polarize(Physical);
                const scope = new TransformScope(children, 'poly');

                // For each child, unmount, transform and then remount outside the group component
                for (const ch of children) {
                    gp.remove(ch);
                    ch.applyTransform(scope, gp.transform());
                    gp.parent.add(ch);
                    affected.push(ch);
                }

                // Remove the group
                gp.parent.remove(gp);
            }

        });

        // Select all affected plus those that were ignored
        dispatch(select, [affected.concat(others).map(x => x.id)]);
    },
    []
);
*/

const topSorter = (a: Physical, b: Physical) => {

    const ap = a.position(RelativeTo.Model);
    const bp = b.position(RelativeTo.Model);
    return ap.y - bp.y;
};

const leftSorter = (a: Physical, b: Physical) => {

    const ap = a.position(RelativeTo.Model);
    const bp = b.position(RelativeTo.Model);
    return ap.x - bp.x;
};

export const leftTopSorter = (a: Physical, b: Physical) => {

    const ab = a.bounds(RelativeTo.Model);
    const bb = b.bounds(RelativeTo.Model);

    if (bb.left > ab.right) return -1;

    const area = Rect.fromPoints([ab.topLeft(), Point.max]);
    if (area.intersects(bb)) {
        return -1;
    }
    else {
        return 1;
    }
};

class Plot implements Geometry {

    public left: number;
    public top: number;
    public width: number;
    public height: number;

    private initial = [] as number[];

    constructor(public readonly id: string, rect: Rect) {
        this.left = rect.left;
        this.top = rect.top;
        this.width = rect.width;
        this.height = rect.height;
        this.initial = [this.left, this.top, this.width, this.height];
    }

    public get changed() {
        return this.initial.some((x, i) => x !== this[i]);
    }

    public moveTo(x: number, y: number) {
        this.left = x;
        this.top = y;
    }

    public intersects(another: Plot) {
        return !!Geometry.intersect(this, another);
    }

    public intersectPoints(another: Geometry): IntersectResult {
        return Geometry.intersect(this, another);
    }

    public toShapeInfo(): any {
        const { left, top, width, height } = this;
        return ShapeInfo.rectangle({ left, top, width, height });
    }
}

type ArrangementStrategy = (gap: number) => ArrangementPlan;
const ArrangementStrategy = (f: ArrangementStrategy) => f;

type ArrangementPlan = {
    sorter: (a: Physical, b: Physical) => number,
    placer: (plot: Plot, bounds: Rect, moved: Plot[]) => void,
};

const distributeVertically = ArrangementStrategy((gap: number) => ({
    sorter: topSorter,
    placer: (plot: Plot, bounds: Rect, moved: Plot[]) => {
        if (moved.length === 0) {
            plot.top = bounds.top;
        }
        else {
            plot.top = Math.max(...moved.map(x => x.top + x.height)) + gap;
        }
    },
}));

const alignVertically = ArrangementStrategy((gap: number) => ({
    sorter: topSorter,
    placer: (plot: Plot, bounds: Rect, moved: Plot[]) => {
        const { x } = bounds.center();
        plot.left = x - plot.width / 2;
    },
}));

const distributeHorizontally = ArrangementStrategy((gap: number) => ({
    sorter: leftSorter,
    placer: (plot: Plot, bounds: Rect, moved: Plot[]) => {
        if (moved.length === 0) {
            plot.left = bounds.left;
        }
        else {
            plot.left = Math.max(...moved.map(x => x.left + x.width)) + gap;
        }
    },
}));

const alignHorizontally = ArrangementStrategy((gap: number) => ({
    sorter: topSorter,
    placer: (plot: Plot, bounds: Rect, moved: Plot[]) => {
        const { y } = bounds.center();
        plot.top = y - plot.height / 2;
    },
}));

const arrange = async (subjects: Physical[], strategy: ArrangementStrategy) => {

    for (let i = 0; i < 10; i++) {

        const plan = strategy((i + 1) * 10);
        const sorted = subjects.slice().sort(plan.sorter);

        const bounds = Physicals.bounds(sorted);
        const plots = sorted.map(x => new Plot(x.id, x.bounds(RelativeTo.Model)));

        const moved = [] as Plot[];

        for (const p of plots) {
            plan.placer(p, bounds, moved);
            moved.push(p);
        }

        if (i >= 9 || moved.some(x => x.changed)) {
            return moved;
        }
    }
};

const createArrangeCommand = (name: string, label: string, strategy: ArrangementStrategy) =>
    defineCommand(
        name,
        label,
        async (r, [targetIds]: [string[]?]) => {

            const objects = r.call(ObjectManager);
            const subjects = resolveSubjects(r, targetIds);
            if (!subjects.length) return;

            const plots = await arrange(subjects, strategy);

            objects.update(() => {
                for (const p of plots) {
                    objects.get(p.id, Physical).moveTo(p.left, p.top);
                }
            });
        }
    );

export const distributeHorizontal = createArrangeCommand('distributeHorizontal', 'Arrange: Distribute Horizontal', distributeHorizontally);
export const distributeVertical = createArrangeCommand('distributeVertical', 'Arrange: Distribute Vertical', distributeVertically);
export const alignHorizontal = createArrangeCommand('alignHorizontal', 'Arrange: Align Horizontal', alignHorizontally);
export const alignVertical = createArrangeCommand('alignVertical', 'Arrange: Align Vertical', alignVertically);

const packTopStrategy = {
    sorter: leftTopSorter,
    placer: (plot: Plot, bounds: Rect, moved: Plot[]) => {
        plot.top = bounds.top;
        for (let i = 0; i < 100; i++) {
            const hit = moved.find(x => x !== plot && plot.intersects(x));
            if (!hit) return;
            plot.top = hit.top + hit.height + 10;
        }
    },
};

const packLeftStrategy = {
    sorter: leftTopSorter,
    placer: (plot: Plot, bounds: Rect, moved: Plot[]) => {
        plot.left = bounds.left;
        for (let i = 0; i < 100; i++) {
            const hit = moved.find(x => x !== plot && plot.intersects(x));
            if (!hit) return;
            plot.left = hit.left + hit.width + 10;
        }
    },
};

const packAxis = async (subjects: Physical[], strategy: typeof packTopStrategy) => {

    subjects = subjects.slice().sort(strategy.sorter);

    const bounds = Physicals.bounds(subjects);
    const plots = subjects.map(x => new Plot(x.id, x.bounds(RelativeTo.Model)));

    const moved = [] as Plot[];

    for (const p of plots) {
        strategy.placer(p, bounds, moved);
        moved.push(p);
        // DebugOverlay.rect(p.id, new Rect(p.left, p.top, p.width, p.height), 'red');
        // await waitFor(100);
    }

    return moved;
};

export const packLeft = defineCommand(
    'packLeft',
    'packLeft',
    async (r, [targetIds]: [string[]?]) => {

        const objects = r.call(ObjectManager);
        const subjects = resolveSubjects(r, targetIds).sort(leftTopSorter);
        if (!subjects.length) return;

        const plots = await packAxis(subjects, packLeftStrategy);

        objects.update(() => {
            for (const p of plots) {
                objects.get(p.id, Physical).moveTo(p.left, p.top);
            }
        });
    }
);

export const packTop = defineCommand(
    'packTop',
    'packTop',
    async (r, [targetIds]: [string[]?]) => {

        const objects = r.call(ObjectManager);
        const subjects = resolveSubjects(r, targetIds).sort(leftTopSorter);
        if (!subjects.length) return;

        const plots = await packAxis(subjects, packTopStrategy);

        objects.update(() => {
            for (const p of plots) {
                objects.get(p.id, Physical).moveTo(p.left, p.top);
                DebugOverlay.clear(p.id);
            }
        });
    }
);
